import React from 'react';
import axios, { isAxiosError } from 'axios';
import {
    Application as MfeApplication,
    ApplicationConfiguration,
    useCurrentAgreement,
    useCurrentLocale,
    useCurrentUser,
} from '@economic/react-app';
import { Locale, Provider as TacoProvider } from '@economic/taco';
import { fetchLocalisation } from '~/fetchLocalisation';
import { INTERNAL_API_URL } from '~/app/shared/util/api/internalApiClient';
import { useCrossSiteTracking } from '~/utilities/useCrossSiteTracking';
import Economic from '../app/components/Economic';
import useTimer from '../app/shared/hooks/useTimer';

const fetchConfiguration = async () => {
    return axios.get<ApplicationConfiguration>(`${INTERNAL_API_URL}/mfe/application`).then(response => response.data);
};

const fetchHeartbeat = async (agreementNumber: number) => {
    return axios.get(`${INTERNAL_API_URL}/mfe/host/heartbeat/${agreementNumber}`);
};

/**
 * Add event listener on both window and firebird window, to also detect the event inside firebird frame
 * @param event name of the event
 * @param fn event handler
 */
const addEventListener = (event: string, fn) => {
    window.addEventListener(event, fn);
    window.top?.firebird.addEventListener(event, fn);
};

axios.interceptors.response.use(undefined, async (error: Error) => {
    if (isAxiosError(error) && error.response?.status === 401) {
        if (error.response.data) {
            window.location.replace(`${error.response.data}?returnUrl=${encodeURIComponent(window.location.href)}`);
        } else {
            window.location.reload();
        }
        return new Promise(() => {
            // Prevent further execution, by pending the promise
        });
    }

    return Promise.reject(error);
});

/**
 * Remove event listener from both window and firebird frame
 * @param event name of the event
 * @param fn event handler
 */
const removeEventListener = (event: string, fn) => {
    window.removeEventListener(event, fn);
    window.top?.firebird.removeEventListener(event, fn);
};

const useSessionHeartbeat = () => {
    const agreement = useCurrentAgreement();
    const loading = React.useRef(false);
    // Attach click event listener after a timeout has passed
    const timer = useTimer(60000, () => addEventListener('click', handleHeartbeat));

    const handleHeartbeat = () => {
        if (!loading.current) {
            loading.current = true;

            fetchHeartbeat(agreement.number).finally(() => {
                loading.current = false;
                removeEventListener('click', handleHeartbeat);
                timer.start();
            });
        }
    };

    React.useEffect(() => {
        const handleTabChange = () => {
            if (document.visibilityState === 'visible') {
                // Add event listener when document is back to visible
                addEventListener('click', handleHeartbeat);
            } else {
                // Remove event listener and stop timer when document is not visible, since listener will be attached when document is back to visible
                removeEventListener('click', handleHeartbeat);
                timer.stop();
            }
        };

        timer.start();
        window.addEventListener('visibilitychange', handleTabChange);

        return () => {
            timer.stop();
            removeEventListener('click', handleHeartbeat);
            window.removeEventListener('visibilitychange', handleTabChange);
        };
    }, []);
};

export default function Application() {
    useSessionHeartbeat();
    useCrossSiteTracking();

    const agreement = useCurrentAgreement();
    const locale = useCurrentLocale();
    const user = useCurrentUser();
    const tacoLocalisation = fetchLocalisation(locale.languageCode as Locale, locale.formatting.date);
    const tacoSettings = { uniqueUserIdentifier: `${agreement.number}_${user.id}` };

    return (
        <TacoProvider localization={tacoLocalisation} settings={tacoSettings}>
            <MfeApplication
                configuration={fetchConfiguration}
                origin={process.env.PUBLIC_URL}
                version={process.env.CLIENT_VERSION ?? 'development'}>
                <Economic />
            </MfeApplication>
        </TacoProvider>
    );
}
